import { Button } from '@ctw/shared/components/button';
import { copyToClipboard } from '@ctw/shared/components/copy-to-clipboard';
import { Heading } from '@ctw/shared/components/heading';
import { notify } from '@ctw/shared/components/toast';
import { type ClassName, tw, twx } from '@ctw/shared/utils/tailwind';
import { faChevronDown, faChevronUp, faCopy } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { uniqueId } from 'lodash-es';
import { type PropsWithChildren, forwardRef, useMemo, useState } from 'react';

export interface CollapsibleCardProps extends PropsWithChildren {
  label: string;
  id?: string;
  testId?: string;
  className?: ClassName;
  initiallyCollapsed?: boolean;
  loading?: boolean;
  controls?: {
    copyToClipboard: {
      content: string;
    };
  };
}

export const CollapsibleCard = forwardRef<HTMLElement, CollapsibleCardProps>(
  ({ label, id, testId, className, initiallyCollapsed, loading, children, controls }, ref) => {
    const [collapsed, setCollapsed] = useState(Boolean(initiallyCollapsed));
    const contentId = useMemo(() => `collapsible-card-${id ?? uniqueId()}`, [id]);

    return (
      <section
        ref={ref}
        id={id}
        data-testid={testId}
        className={twx(
          'flex flex-col overflow-hidden rounded-lg border border-divider-main bg-white px-6 shadow-sm',
          className,
        )}
      >
        {loading && (
          <>
            <div className="flex flex-col space-y-1.5 p-6">
              <div className="h-6 w-1/3 animate-pulse rounded-md bg-content-subtle/10" />
            </div>
            <div className="p-6 pt-0">
              <div className="mb-2 h-4 w-full animate-pulse rounded-md bg-content-subtle/10" />
              <div className="mb-2 h-4 w-full animate-pulse rounded-md bg-content-subtle/10" />
              <div className="h-4 w-3/4 animate-pulse rounded-md bg-content-subtle/10" />
            </div>
          </>
        )}
        {!loading && (
          <>
            <div className={tw`flex items-center justify-between pt-2`}>
              <Heading
                level="h4"
                className={tw`!pl-0 mx-0 font-semibold text-content-subtle text-xs uppercase leading-tight tracking-wider`}
              >
                {label}
              </Heading>
              <div className={tw`flex items-center gap-2`}>
                {controls?.copyToClipboard && (
                  <Button
                    type="button"
                    variant="secondary"
                    size="lg"
                    className={tw`flex items-center justify-center border-0 p-[0.5rem] shadow-none`}
                    ariaLabel="Copy to clipboard"
                    onClick={() =>
                      copyToClipboard(controls.copyToClipboard.content, () => {
                        notify({
                          type: 'info',
                          title: `Copied content to clipboard: ${label}`,
                        });
                      })
                    }
                  >
                    <FontAwesomeIcon
                      icon={faCopy}
                      className={tw`h-[0.9rem] w-[0.9rem] text-content-main`}
                    />
                  </Button>
                )}
                <Button
                  type="button"
                  variant="secondary"
                  size="lg"
                  className={tw`flex items-center justify-center border-0 p-[0.5rem] shadow-none`}
                  ariaLabel={collapsed ? 'Show content' : 'Hide content'}
                  ariaExpanded={!collapsed}
                  ariaControls={contentId}
                  onClick={() => setCollapsed((prev) => !prev)}
                >
                  <FontAwesomeIcon
                    icon={collapsed ? faChevronUp : faChevronDown}
                    stroke="currentColor"
                    strokeWidth={25}
                    className={tw`h-2.5 w-2.5 text-content-main`}
                  />
                </Button>
              </div>
            </div>
            <div
              tabIndex={-1}
              id={contentId}
              className={twx('flex flex-col pb-6', {
                hidden: collapsed,
              })}
            >
              {children}
            </div>
          </>
        )}
      </section>
    );
  },
);
